/**
 * MusicPlaylistDetailPage - プレイリスト詳細（収録曲一覧）
 *
 * 収録曲はサーバーから登録順で返るため、そのまま再生キューへ渡せる。
 * 名前の変更・プレイリストの削除はヘッダーのメニューから行う。
 */

import { ListMusic, Pencil, Play, Shuffle, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'

import { deleteMusicPlaylist, getMusicPlaylistDetail, removeMusicPlaylistTrack, renameMusicPlaylist } from '@/api/api'
import type { MusicPlaylistDetailResponse } from '@/api/type'
import { Header } from '@/components/shared/header'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { AuthImage } from '@/components/ui/auth-image'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { PATHS } from '@/routes/paths'
import { useMusicStore } from '@/store/useMusicStore'

import { ACCENT_COLOR } from './constants'
import { formatTotalDuration } from './format'
import { pickRandomTracks, sliceQueueWindow, toMusicTrack } from './queue-track'
import { MusicTrackMenu } from './track-menu'

export const MusicPlaylistDetailPage = () => {
  const navigate = useNavigate()
  const playQueue = useMusicStore((s) => s.playQueue)
  const { playlist_id: playlistIdParam } = useParams<{ playlist_id: string }>()
  const playlistId = Number(playlistIdParam)

  const [playlist, setPlaylist] = useState<MusicPlaylistDetailResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isRenaming, setIsRenaming] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const { isSubmitting, run } = useSubmitLock()

  useEffect(() => {
    if (!Number.isFinite(playlistId)) {
      setError('プレイリストが指定されていません')
      setIsLoading(false)
      return
    }

    let cancelled = false
    getMusicPlaylistDetail(playlistId)
      .then((res) => {
        if (!cancelled) setPlaylist(res)
      })
      .catch(() => {
        if (!cancelled) setError('プレイリストの取得に失敗しました')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [playlistId])

  const tracks = useMemo(() => playlist?.tracks ?? [], [playlist])

  // 曲を直接選んだときは1曲だけ再生する（続けて聴きたい場合は上の再生/シャッフルを使う）
  const handlePlayFrom = (index: number) => {
    const track = tracks.at(index)
    if (!track) return
    playQueue([toMusicTrack(track)], 0)
  }

  const handlePlayAll = () => {
    if (tracks.length === 0) return
    playQueue(sliceQueueWindow(tracks, 0).items.map(toMusicTrack), 0)
  }

  const handleShuffle = () => {
    if (tracks.length === 0) return
    playQueue(pickRandomTracks(tracks).map(toMusicTrack), 0, { shuffle: true })
  }

  const handleRename = () => {
    const name = newName.trim()
    if (!name) return

    void run(async () => {
      try {
        await renameMusicPlaylist(playlistId, name)
        setPlaylist((prev) => (prev === null ? prev : { ...prev, name }))
        setIsRenaming(false)
      } catch {
        toast.error('名前の変更に失敗しました')
      }
    })
  }

  const handleDelete = () => {
    deleteMusicPlaylist(playlistId)
      .then(() => navigate(PATHS.MUSIC.PLAYLISTS))
      .catch(() => toast.error('プレイリストの削除に失敗しました'))
  }

  const handleRemoveTrack = (episodeId: number) => {
    removeMusicPlaylistTrack(playlistId, episodeId)
      .then(() => {
        setPlaylist((prev) =>
          prev === null ? prev : { ...prev, tracks: prev.tracks.filter((t) => t.episode_id !== episodeId) },
        )
      })
      .catch(() => toast.error('曲の削除に失敗しました'))
  }

  const totalDuration = tracks.reduce((sum, t) => sum + t.duration, 0)

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Header
        title=""
        onClose={() => navigate(PATHS.MUSIC.PLAYLISTS)}
        dropdownSections={
          playlist === null
            ? undefined
            : [
                {
                  type: 'action',
                  label: 'プレイリスト',
                  items: [
                    {
                      key: 'rename',
                      label: '名前を変更',
                      icon: <Pencil size={18} />,
                      onClick: () => {
                        setNewName(playlist.name)
                        setIsRenaming(true)
                      },
                    },
                    {
                      key: 'delete',
                      label: 'プレイリストを削除',
                      icon: <Trash2 size={18} />,
                      onClick: () => setIsDeleteOpen(true),
                    },
                  ],
                },
              ]
        }
      />

      {isLoading && (
        <div className="flex flex-1 justify-center py-12">
          <Spinner size={40} />
        </div>
      )}
      {!isLoading && error && <div className="flex-1 py-12 text-center text-muted-foreground">{error}</div>}

      {!isLoading && !error && playlist && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col items-center px-8 pt-2">
            <div className="aspect-square w-full max-w-70 overflow-hidden rounded-lg bg-white/10">
              {playlist.thumbnail ? (
                <AuthImage
                  src={playlist.thumbnail}
                  alt={playlist.name}
                  lazy={false}
                  className="size-full object-cover"
                  cacheKey={`music-playlist-${playlist.id}`}
                />
              ) : (
                <div className="flex size-full items-center justify-center">
                  <ListMusic size={48} className="text-muted-foreground" />
                </div>
              )}
            </div>

            <div className="mt-4 text-center text-xl font-bold">{playlist.name}</div>
          </div>

          <div className="mt-5 flex items-center justify-center gap-4 px-4">
            <button
              type="button"
              aria-label="シャッフル再生"
              onClick={handleShuffle}
              className="flex size-12 items-center justify-center rounded-full border-none bg-white/10"
              style={{ color: ACCENT_COLOR }}
            >
              <Shuffle size={20} />
            </button>
            <button
              type="button"
              onClick={handlePlayAll}
              className="flex min-w-45 items-center justify-center gap-2 rounded-full border-none bg-white py-3 font-semibold text-black"
            >
              <Play size={18} fill="black" />
              再生
            </button>
          </div>

          <ul className="mt-5 pl-4">
            {playlist.tracks.map((track, i) => (
              <li key={track.episode_id} className="flex items-center gap-3">
                <span className="flex min-w-0 flex-1 items-center gap-2 border-b border-white/10 pr-4">
                  <button
                    type="button"
                    onClick={() => handlePlayFrom(i)}
                    className="min-w-0 flex-1 border-none py-3 text-left"
                  >
                    <span className="block truncate">{track.title}</span>
                    <span className="block truncate text-sm text-muted-foreground">
                      {track.artist ?? track.album_title}
                    </span>
                  </button>
                  <MusicTrackMenu
                    track={toMusicTrack(track)}
                    onRemoveFromPlaylist={() => handleRemoveTrack(track.episode_id)}
                  />
                </span>
              </li>
            ))}
          </ul>

          <div className="px-4 py-4 text-sm text-muted-foreground">
            {playlist.tracks.length}曲、{formatTotalDuration(totalDuration)}
          </div>
        </div>
      )}

      <BottomSheet title="名前を変更" open={isRenaming} onOpenChange={setIsRenaming} snapPoints={[0.4]}>
        <div className="flex gap-2 px-4 pb-8">
          <Input
            autoFocus
            value={newName}
            maxLength={200}
            placeholder="プレイリスト名"
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleRename()}
          />
          <Button disabled={!newName.trim() || isSubmitting} onClick={handleRename}>
            保存
          </Button>
        </div>
      </BottomSheet>

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>プレイリストを削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              「{playlist?.name}」を削除します。曲そのものは削除されません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>削除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
